import {
  usePointOfInterestsCountByDueDateQuery
} from '@/__generated__/graphql';
import { useWindowSize } from '@/hooks/useWindowSize';
import { POI_DUE_DATE_COLOR_MAP } from '@/theme/colors';
import { titleCase } from '@/utils';
import { useMemo } from 'react';
import { PieChartWidget } from '../shared/PieChartWidget';
import { getDueDateColors } from '../styles';
import { PoiSummaryWidgetsProps } from '../StatusWidget/StatusWidget';

export const DueDateWidget = ({ queryInput, widgetWidth = '0' }: PoiSummaryWidgetsProps) => {
  const screenSize = useWindowSize();

  const { loading, data } = usePointOfInterestsCountByDueDateQuery({
    fetchPolicy: 'network-only',
    variables: { input: queryInput },
  });

  const dueDateCounts = useMemo(() => {
    const dueDateCount = data?.pointOfInterestsCountByDueDate;
    if (dueDateCount?.every((item) => item?.count === null)) {
      return [{ key: 'N/A', count: 1 }];
    }
    return dueDateCount?.map((item) => ({
      count: item?.count,
      key: item?.key,
    }));
  }, [data?.pointOfInterestsCountByDueDate]);

  const size = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return {
        outerRadius: 52,
        innerRadius: 43,
      };
    }
    return {
      outerRadius: 75,
      innerRadius: 62,
    };
  }, [screenSize]);

  const scale = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 0.7;
    }
    return 1;
  }, [screenSize]);

  const legendKeys = useMemo(() => {
    return Object.keys(POI_DUE_DATE_COLOR_MAP).map((key) => titleCase(key.replaceAll('_', ' ')));
  }, []);

  const { highCount, highText } = useMemo(() => {
    return (
      legendKeys
        .map((key) => ({
          highText: key,
          highCount: dueDateCounts?.find((item) => item.key === key)?.count ?? 0,
        }))
        .find((key) => key.highCount) ?? { highCount: 0, highText: legendKeys[0] }
    );
  }, [dueDateCounts, legendKeys]);

  return (
    <PieChartWidget
      widgetWidth={widgetWidth}
      loading={loading}
      chartScale={scale}
      data={dueDateCounts}
      chartSize={size}
      colorMapper={getDueDateColors}
      title="Due Date"
      centerText={highText}
      centerValue={highCount}
      legendKeys={legendKeys}
    />
  );
};
